// components/SystemSelectorModal.tsx
import React, { useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  MenuItem,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import BWITextField from './shared/BWITextField';
import { ApiSystem, System } from '../state/types';
import { useSession } from '../context/SessionContext';
import { groupAndFilterSystems } from './system/systemHelpers';

interface Props {
  open: boolean;
  onClose: () => void;
}

const SystemSelectorModal: React.FC<Props> = ({
  open,
  onClose,
}) => {

  const { user, systems, selectedSystem, selectSystem } = useSession();

  const [search, setSearch] = React.useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [open]);

  const accessibleCodes =
    user?.role === "Admin"
      ? systems.map(s => s.systemCode)
      : (user?.system_access ?? []);

  const filteredGroups = groupAndFilterSystems(systems, accessibleCodes, search);

  const handleSelectSystem = (sysCode: string) => {
    const system = systems.filter((system: ApiSystem) => system.systemCode === sysCode)[0]
    selectSystem(system)
  }

  if (!systems || systems.length === 0) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Select a System</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            Loading systems...
          </Typography>
        </DialogContent>
      </Dialog>
    );
  }


  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Select a System</DialogTitle>
      <DialogContent dividers>
        <BWITextField
          startIcon={<SearchIcon />}
          placeholder="Search Systems..."
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          inputRef={searchInputRef}
          fullWidth
          sx={{ mb: 2 }}
        />
        {filteredGroups.map((group) => (
          <Box key={group.label} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              {group.label}
            </Typography>
            {group.systems.map(({ code, label }) => (
              <MenuItem
                key={code}
                selected={code === selectedSystem?.systemCode}
                onClick={() => {
                  handleSelectSystem(code);
                  onClose();
                }}
              >
                <Typography variant="body1">{label}</Typography>
              </MenuItem>
            ))}
          </Box>
        ))}
        {filteredGroups.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No systems match your search.
          </Typography>
        )}

      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SystemSelectorModal;
